import React, { useState } from 'react';
import { uniq } from 'lodash';
import styles from './BulkEdit.module.scss';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import LiteralsService from 'src/services/literals/LiteralsService';

import { ButtonSecondaryDark, Checkbox, IconClose } from '@americanexpress/dls-react';
import { DLSSize } from 'src/types/dls-size.enum';
import SmallModal from 'src/components/small-modal/SmallModal';
import { TransactionModel } from 'src/types/transaction.model';
import SelectDetailed from 'src/components/formik-fields/selectDetailed/SelectDetailed';
import { InitialFormValues } from 'src/types/view-models/small-modal.view.model';
import TransactionService, { frequencies, Frequency } from 'src/services/transaction.service';
import { Form, Formik } from 'formik';
import { CategoryModel, ClickableCategoryModel } from 'src/types/category.model';
import CategoryService from 'src/services/category.service';
import { useUpdateTransactions, useUpdateTransactionsGroup } from 'src/hooks/queries/transactions';
import CustomSubcategoryModal from 'src/components/atomic/organisms/transaction/custom-subcategory-modal/CustomSubcategoryModal';
import { CustomSubcategoryModalStateModel } from 'src/types/custom-subcategory-modal-state.model';
import IconButton from 'src/components/icon-button/IconButton';
import { ButtonType } from 'src/types/button-type.enum';
import { useCreateRules } from 'src/hooks/queries/rules';
import { trackClick } from 'src/hooks/useAdobeTags';
import { useLocation } from 'react-router-dom';
import adobeTags from 'src/types/adobe-tags';
import * as Yup from 'yup';
import Selectors from 'src/utils/selectors';
import { RuleMatcher } from 'src/types/ruleMatcher.enum';
import { useCategories } from 'src/hooks/queries/categories';
import InfoBox from 'src/components/atomic/molecules/infoBox/InfoBox';
import { useSelectedTransactionsContext } from 'src/hooks/useSelectedTransactions';

export interface BulkEditComponentProps {
    selectedTransactionCount: Number;
    setShowChangeCategory: (value: boolean) => void;
    setShowChangeFrequency: (value: boolean) => void;
    changeCategory: JSX.Element;
    changeFrequency: JSX.Element;
    pathname: string;
    onClose: () => void | Promise<void>;
}

export interface BulkEditProps {
    selectedTransactions: TransactionModel[];
    onClose: () => void;
}

export function BulkEditComponent({
    selectedTransactionCount,
    setShowChangeCategory,
    setShowChangeFrequency,
    changeCategory,
    changeFrequency,
    pathname,
    onClose
}: BulkEditComponentProps): JSX.Element {
    return (
        <div>
            <div className={styles.bulkBar} data-cy={Selectors.Transactions.BULK_EDIT_CONTAINER}>
                <div className={styles.bulkBarLeft}>
                    <Typography variant={TypographyVariants.SUBTEXT} className={styles.bannerText} aria-live="polite">
                        {LiteralsService.getLiteral('TransactionsTable.bulkEdit.bulkSelected', [
                            selectedTransactionCount
                        ])}
                    </Typography>
                    <ButtonSecondaryDark
                        size={DLSSize.SMALL}
                        onClick={() => {
                            setShowChangeCategory(true);
                            trackClick(pathname, adobeTags.Transactions.Bulk.Category);
                        }}
                        id="change-category-button"
                        data-cy={Selectors.Transactions.BULK_EDIT_CHANGE_CATEGORY_SOURCE_BUTTON}
                    >
                        {LiteralsService.getLiteral('TransactionsTable.bulkEdit.changeCategory')}
                    </ButtonSecondaryDark>
                    <ButtonSecondaryDark
                        size={DLSSize.SMALL}
                        onClick={() => {
                            setShowChangeFrequency(true);
                            trackClick(pathname, adobeTags.Transactions.Bulk.Frequency);
                        }}
                        id="change-frequency-button"
                        data-cy={Selectors.Transactions.BULK_EDIT_CHANGE_FREQUENCY_BUTTON}
                    >
                        {LiteralsService.getLiteral('TransactionsTable.bulkEdit.changeFrequency')}
                    </ButtonSecondaryDark>
                </div>
                <IconButton
                    variant={ButtonType.TERTIARY_DARK}
                    icon={<IconClose />}
                    data-cy="bulkTransactionsCloseSelect"
                    title={LiteralsService.getLiteral('TransactionsTable.bulkEdit.closeBulkEdit')}
                    className={styles.closeIcon}
                    onClick={onClose}
                />
            </div>
            {changeCategory}
            {changeFrequency}
        </div>
    );
}

export default function BulkEdit({ onClose, selectedTransactions }: BulkEditProps): JSX.Element {
    const [showChangeCategory, setShowChangeCategory] = useState<boolean>(false);
    const [showChangeFrequency, setShowChangeFrequency] = useState<boolean>(false);
    const [createBulkRule, setCreateBulkRule] = useState<boolean>(false);
    const [customSubcategoryModalTransactions, setCustomSubcategoryModalTransactions] =
        useState<CustomSubcategoryModalStateModel<TransactionModel[]>>(null);
    const { pathname } = useLocation();

    const { isLoading: isLoadingCategories, data: categories } = useCategories();
    const { mutateAsync: updateTransactionsGroup } = useUpdateTransactionsGroup();
    const { mutateAsync: updateTransactions } = useUpdateTransactions();
    const { mutateAsync: createRules } = useCreateRules();
    const { setAllTransactionsSelect } = useSelectedTransactionsContext();

    if (isLoadingCategories) return null;

    let createRuleLabelElement: JSX.Element | null = null;
    let names: string[] = [];
    if (selectedTransactions?.length > 0) {
        names = selectedTransactions.map((transaction: TransactionModel) => transaction.merchantName);
        names = uniq(names);
        const shortenedList = names.slice(0, 3);

        createRuleLabelElement = (
            <span>
                {LiteralsService.getLiteral('TransactionsTable.bulkEdit.addRule1')}
                {shortenedList.map((name: string, index: number) => (
                    <span key={name}>
                        <b>{name}</b>
                        {index < shortenedList.length - 1 ? ', ' : ''}
                    </span>
                ))}
                {names.length > 3
                    ? LiteralsService.getLiteral('TransactionsTable.bulkEdit.addRule3')
                    : LiteralsService.getLiteral('TransactionsTable.bulkEdit.addRule2')}
            </span>
        );
    }

    /* category options with links */
    const categoryOptions: ClickableCategoryModel[] = CategoryService.getCategoryOptions<TransactionModel[]>(
        categories,
        selectedTransactions,
        setCustomSubcategoryModalTransactions
    );

    const handleCategorySubmit = async (values: InitialFormValues): Promise<void> => {
        if (createBulkRule)
            await createRules(
                selectedTransactions.map((t: TransactionModel) => {
                    return {
                        matcher: RuleMatcher.CONTAINS,
                        transactionName: t.merchantName,
                        groupUid: values.changeToGroupUid
                    };
                })
            );
        await updateTransactionsGroup({ transactions: selectedTransactions, groupUid: values.changeToGroupUid });
        setCreateBulkRule(false);
        setShowChangeCategory(false);
        setAllTransactionsSelect(false);
    };

    const changeCategory = showChangeCategory ? (
        <Formik
            initialValues={{
                changeToGroupUid: '',
                moveAll: false
            }}
            onSubmit={handleCategorySubmit}
            validationSchema={Yup.object({
                changeToGroupUid: Yup.string().required(LiteralsService.getLiteral('FormErrors.categoryRequired'))
            })}
        >
            {({ submitForm, setFieldValue }) => (
                <>
                    {customSubcategoryModalTransactions && (
                        <CustomSubcategoryModal
                            categories={categories}
                            parentCategoryId={customSubcategoryModalTransactions.parentCategoryId}
                            closeModal={async (category?: CategoryModel) => {
                                if (category?.id) setFieldValue('changeToGroupUid', category.id);
                                setCustomSubcategoryModalTransactions(null);
                            }}
                        />
                    )}
                    {!customSubcategoryModalTransactions && (
                        <Form>
                            <SmallModal
                                onClose={() => {
                                    setShowChangeCategory(false);
                                    trackClick(pathname, adobeTags.Transactions.ChangeCategoryModal.Cancel);
                                }}
                                id="changeCategoryModal"
                                data-cy={Selectors.Transactions.CHANGE_CATEGORY_MODAL}
                                aria-label="bulk-edit"
                                headerText={LiteralsService.getLiteral('TransactionsTable.bulkEdit.changeCategory?')}
                                submitButtonText={LiteralsService.getLiteral('CommonText.apply')}
                                onSubmit={async () => {
                                    await submitForm();
                                    trackClick(pathname, adobeTags.Transactions.ChangeCategoryModal.Yes);
                                }}
                            >
                                {
                                    <SelectDetailed
                                        id="changeToGroupUid"
                                        className={styles.bulkSelect}
                                        label={LiteralsService.getLiteral('Budget.Create.categoryLabel')}
                                        options={categoryOptions}
                                        tagName={adobeTags.Transactions.ChangeCategoryModal.Dropdown}
                                        nested
                                    />
                                }
                                <div className={styles.checkBoxRow}>
                                    <Checkbox
                                        className={styles.checkbox}
                                        id="moveAll"
                                        label={createRuleLabelElement}
                                        onChange={(e: any) => {
                                            setCreateBulkRule(e.target.checked);
                                            trackClick(
                                                pathname,
                                                adobeTags.Transactions.ChangeCategoryModal[
                                                    e.target.checked ? 'Check' : 'Uncheck'
                                                ]
                                            );
                                        }}
                                    />
                                </div>
                                {createBulkRule && (
                                    <InfoBox text={LiteralsService.getLiteral('TransactionsTable.bulkEdit.ruleInfo')} />
                                )}
                            </SmallModal>
                        </Form>
                    )}
                </>
            )}
        </Formik>
    ) : null;

    const handleFrequencySubmit = async (values: InitialFormValues): Promise<void> => {
        await updateTransactions(
            selectedTransactions.map((t: TransactionModel) =>
                TransactionService.updateFrequency(t, values.changeFrequencySelect)
            )
        );
        setShowChangeFrequency(false);
        setAllTransactionsSelect(false);
    };

    const changeFrequency = showChangeFrequency ? (
        <Formik
            initialValues={{
                changeFrequencySelect: Frequency.TYPICAL
            }}
            onSubmit={handleFrequencySubmit}
        >
            {({ submitForm }) => (
                <Form>
                    <SmallModal
                        onClose={() => {
                            setShowChangeFrequency(false);
                            trackClick(pathname, adobeTags.Transactions.ChangeFrequencyModal.Cancel);
                        }}
                        id="changeFrequencyModal"
                        data-cy={Selectors.Transactions.CHANGE_FREQUENCY_MODAL}
                        headerText={LiteralsService.getLiteral('TransactionsTable.bulkEdit.changeFrequency?')}
                        onSubmit={async () => {
                            await submitForm();
                            trackClick(pathname, adobeTags.Transactions.ChangeFrequencyModal.Yes);
                        }}
                    >
                        <SelectDetailed
                            id="changeFrequencySelect"
                            className={styles.bulkSelect}
                            label={LiteralsService.getLiteral('TransactionDetails.frequency')}
                            options={frequencies}
                            tagName={adobeTags.Transactions.ChangeFrequencyModal.Dropdown}
                        />
                    </SmallModal>
                </Form>
            )}
        </Formik>
    ) : null;

    return (
        <BulkEditComponent
            selectedTransactionCount={selectedTransactions?.length}
            setShowChangeCategory={setShowChangeCategory}
            setShowChangeFrequency={setShowChangeFrequency}
            changeCategory={changeCategory}
            changeFrequency={changeFrequency}
            pathname={pathname}
            onClose={onClose}
        />
    );
}
